import React from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { useSelector } from 'react-redux';
import chroma from 'chroma-js';
import { getFilledButtonText, getReadableButtonColor } from '../theme/buttonContrast';

const StyledButton = styled(Button)(({ theme, variant, disabled, startIcon, endIcon, color = 'primary' }) => {
    const businessPreference = useSelector((state) => state.core.businessData);
    const mainColor = color !== 'primary' && theme.palette[color]?.main
        ? theme.palette[color].main
        : chroma.valid(businessPreference?.mainColor) ? businessPreference.mainColor : theme.palette.primary.main;
    const darkColor = () => chroma(mainColor).darken(0.8).hex();
    const lightColor = () => chroma(mainColor).brighten(0.3).hex();
    const isDark = theme.palette.mode === 'dark';

    return {
        zIndex: 100,
        width: '100%',
        minWidth: '40px',
        background: disabled && variant !== 'text'
            ? (isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)') 
            : variant === 'contained' 
                ? `linear-gradient(135deg, ${lightColor()}, ${mainColor})` 
                : 'transparent',
        borderRadius: '7px',
        maxWidth: '250px',
        padding: '7px 12px',
        border: variant === 'outlined'
            ? `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`
            : 'none',
        boxShadow: variant === 'contained'
            ? `0 1px 2px rgba(0, 0, 0, 0.08), 0 2px 6px ${chroma(mainColor).alpha(0.18).css()}`
            : 'none',
        color: variant === 'contained'
            ? getFilledButtonText(lightColor(), mainColor)
            : color === 'inherit' ? 'inherit' : getReadableButtonColor(mainColor, theme.palette.background.paper),
        fontWeight: 600,
        fontSize: '13px',
        letterSpacing: '-0.01em',
        textTransform: 'none',
        transition: 'all 180ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            color: variant === 'contained'
                ? getFilledButtonText(mainColor, darkColor())
                : color === 'inherit' ? 'inherit' : getReadableButtonColor(mainColor, theme.palette.background.paper),
            background: variant === 'contained' 
                ? `linear-gradient(135deg, ${mainColor}, ${darkColor()})` 
                : (isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)'),
            boxShadow: variant === 'contained'
                ? `0 2px 4px rgba(0, 0, 0, 0.1), 0 5px 12px ${chroma(mainColor).alpha(0.22).css()}`
                : 'none',
            transform: 'translateY(-0.5px)',
            cursor: 'pointer',
        },
        '&:active': {
            transform: 'translateY(0) scale(0.98)',
            boxShadow: variant === 'contained'
                ? `0 1px 2px rgba(0, 0, 0, 0.1), 0 2px 5px ${chroma(mainColor).alpha(0.16).css()}`
                : 'none',
        },

        '.button-icon': {
            display: 'none',
        },
        '.button-text': {
            display: 'inline',
        },
        '& .MuiButton-startIcon': {
            marginRight: startIcon ? '6px' : '0',
            '& svg': {
                fontSize: '16px',
            },
        },
        '& .MuiButton-endIcon': {
            marginLeft: endIcon ? '6px' : '0',
            '& svg': {
                fontSize: '16px',
            },
        },
    };
});

const GlassButton = ({ children, variant = 'contained', startIcon, endIcon, ...props }) => {
   

    return (
        <StyledButton
            variant={variant}
            startIcon={startIcon}
            endIcon={endIcon}
            {...props}
        >
            {children}
        </StyledButton>
    );
};

export default GlassButton;
